import { useEffect, useMemo, useState } from 'react';
import { Alert, Pressable, View, Platform, InteractionManager, Keyboard } from 'react-native';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import { doc, serverTimestamp, setDoc } from 'firebase/firestore';
import { Screen } from '@/components/layout/Screen';
import { Button } from '@/components/ui/Button';
import { Body, Caption, ThemedText } from '@/components/ui/Text';
import { useTheme } from '@/theme';
import { TravelType, useUserStore } from '@/state/userStore';
import { db } from '@/lib/firebase';
import { OnboardingProgress } from '@/components/onboarding/OnboardingProgress';
import { getOnboardingStep, getTotalOnboardingSteps } from '@/constants/onboarding';
import { LoadingState } from '@/components/ui/LoadingState';
import { withTimeout } from '@/utils/withTimeout';
import { useOnboardingStepAnalytics } from '@/hooks/useOnboardingStepAnalytics';

const TRAVEL_TYPES: { id: TravelType; label: string; description: string }[] = [
  {
    id: 'solo',
    label: 'Solo',
    description: 'Flexible pacing with quiet time baked in.',
  },
  {
    id: 'group',
    label: 'Group',
    description: 'Shared planning, polls, and coordination tools.',
  },
  {
    id: 'business',
    label: 'Business',
    description: 'Meetings, expense caps, and downtime slots.',
  },
  {
    id: 'couple',
    label: 'Couple',
    description: 'Romantic highlights and balanced itineraries.',
  },
  {
    id: 'family',
    label: 'Family',
    description: 'Kid-friendly picks and multi-age pacing.',
  },
];

const TRAVEL_TYPE_SAVE_TIMEOUT_MS = 8000;

export default function TravelTypeScreen() {
  const router = useRouter();
  const params = useLocalSearchParams();
  const theme = useTheme();
  const existingType = useUserStore((state) => state.profile.travelType);
  const firebaseUser = useUserStore((state) => state.firebaseUser);
  const setTravelType = useUserStore((state) => state.setTravelType);
  const completeOnboarding = useUserStore((state) => state.completeOnboarding);
  const isNewUser = useUserStore((state) => state.isNewUser);
  const hasCompletedOnboarding = useUserStore((state) => state.hasCompletedOnboarding);
  const authReady = useUserStore((state) => state.authReady);

  const [selectedType, setSelectedType] = useState<TravelType | undefined>(existingType);
  const [saving, setSaving] = useState(false);

  const rawMode = Array.isArray(params?.mode) ? params.mode[0] : params?.mode;
  const rawReturnTo = Array.isArray(params?.returnTo) ? params.returnTo[0] : params?.returnTo;
  const isEditMode = rawMode === 'edit';
  const { logCompleted } = useOnboardingStepAnalytics('travel-type', !isEditMode);
  const returnTo = useMemo(() => {
    if (rawReturnTo && typeof rawReturnTo === 'string') {
      try {
        return decodeURIComponent(rawReturnTo);
      } catch {
        return rawReturnTo;
      }
    }
    return '/(tabs)/profile';
  }, [rawReturnTo]);

  const navigateAfterSettledUi = async (target: string) => {
    Keyboard.dismiss();
    await new Promise<void>((resolve) => {
      InteractionManager.runAfterInteractions(() => resolve());
    });
    router.replace(target);
  };

  const handleFinish = async (type?: TravelType) => {
    setTravelType(type);

    if (!firebaseUser?.uid) {
      if (!isEditMode) {
        completeOnboarding();
        logCompleted();
      }
      await navigateAfterSettledUi(isEditMode ? returnTo : '/(tabs)');
      return;
    }

    setSaving(true);

    try {
      const payload: Record<string, any> = {
        travelType: type ?? null,
        updatedAt: serverTimestamp(),
      };

      // first-trip is the default finale; email sync is optional from there
      await withTimeout(
        setDoc(doc(db, 'users', firebaseUser.uid), payload, { merge: true }),
        TRAVEL_TYPE_SAVE_TIMEOUT_MS,
        'travel type save',
      );

      if (!isEditMode) {
        // Continue to first-trip template picker
        logCompleted();
        await navigateAfterSettledUi('/(onboarding)/first-trip');
      } else {
        await navigateAfterSettledUi(returnTo);
      }
    } catch (error) {
      console.error('Failed to save travel type', error);
      Alert.alert(
        'Saving offline',
        'Could not save your travel preference right now, but we saved it locally so you can continue.',
      );
      if (!isEditMode) {
        logCompleted();
        await navigateAfterSettledUi('/(onboarding)/first-trip');
      } else {
        await navigateAfterSettledUi(returnTo);
      }
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    if (!isEditMode && authReady && !isNewUser && hasCompletedOnboarding) {
      router.replace('/(tabs)');
    }
  }, [authReady, hasCompletedOnboarding, isEditMode, isNewUser, router]);

  if (!isEditMode && authReady && !isNewUser && hasCompletedOnboarding) {
    return <LoadingState variant="fullscreen" message="Opening Tripvio…" />;
  }

  const currentStep = getOnboardingStep('travel-type') ?? 4;
  const totalSteps = getTotalOnboardingSteps();
  const showProgress = !isEditMode;
  const screenTitle = isEditMode ? 'Update travel style' : 'How do you usually travel?';
  const screenSubtitle = isEditMode
    ? 'Adjust your default travel style to tune recommendations.'
    : 'This helps Tripvio tune recommendations, budgets, and day pacing.';

  return (
    <Screen
      title={screenTitle}
      subtitle={screenSubtitle}
      showBackButton={true}
      onBack={() => {
        if (isEditMode) {
          router.replace(returnTo);
        } else {
          router.replace('/(onboarding)/avatar');
        }
      }}
    >
      {showProgress && <OnboardingProgress currentStep={currentStep} totalSteps={totalSteps} />}
      <View style={{ gap: theme.spacing(1.5) }}>
        {TRAVEL_TYPES.map((option) => {
          const isSelected = option.id === selectedType;

          return (
            <Pressable
              key={option.id}
              accessibilityRole={Platform.OS === 'android' ? 'button' : 'radio'}
              accessibilityState={{ selected: isSelected }}
              onPress={() => setSelectedType(option.id)}
              style={({ pressed }) => ({
                padding: theme.spacing(1.75),
                borderRadius: theme.radii.lg,
                borderWidth: 1,
                borderColor: isSelected ? theme.colors.primary : theme.colors.borderMuted,
                backgroundColor: isSelected ? theme.colors.surface : theme.colors.background,
                gap: theme.spacing(0.75),
                opacity: pressed ? 0.9 : 1,
              })}
            >
              <ThemedText variant="title" color={theme.colors.text}>
                {option.label}
              </ThemedText>
              <Caption color={theme.colors.textSecondary}>{option.description}</Caption>
            </Pressable>
          );
        })}
      </View>

      <Body color={theme.colors.textSecondary}>
        You can edit this anytime in Settings. It helps us personalize AI itineraries, budget alerts, and notification cadence.
      </Body>

      <Button
        label={isEditMode ? 'Save preference' : 'Continue'}
        onPress={() => handleFinish(selectedType)}
        disabled={!selectedType || saving}
        style={{ opacity: selectedType && !saving ? 1 : 0.6 }}
      />
      <Button
        label={isEditMode ? 'Back to profile' : 'Skip for now'}
        variant="ghost"
        onPress={() => {
          if (isEditMode) {
            router.replace(returnTo);
          } else {
            handleFinish();
          }
        }}
        disabled={saving}
      />
    </Screen>
  );
}


